import React, { useState } from "react";
import {
  Card,
  Input,
  Button,
  Table,
  Typography,
  Space,
  Alert,
  Spin,
  Result,
  Tag,
  Row,
  Col
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { publicService } from "../../services/publicService";
import { formatPrice, formatTotalPrice, formatDate } from "../../utils/formatters";

const { Title, Text } = Typography;

const PublicOrderDetailsPage = () => {
  const [publicToken, setPublicToken] = useState("");
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);

  // Status options for display
  const orderStatusLabels = {
    NEW: { label: "Yeni", color: "blue" },
    IN_PROGRESS: { label: "Devam Ediyor", color: "orange" },
    COMPLETED: { label: "Tamamlandı", color: "green" },
    CANCELLED: { label: "İptal Edildi", color: "red" },
    FAILED: { label: "Başarısız", color: "red" },
  };

  const itemStatusLabels = {
    NEW: { label: "Yeni", color: "blue" },
    IN_PROGRESS: { label: "Devam Ediyor", color: "orange" },
    COMPLETED: { label: "Tamamlandı", color: "green" },
    CANCELLED: { label: "İptal Edildi", color: "red" },
  };

  const fetchOrderDetails = async () => {
    if (!publicToken.trim()) {
      setError("Lütfen sipariş kodunu girin");
      return;
    }

    if (publicToken.length !== 10) {
      setError("Sipariş kodu 10 haneli olmalıdır");
      return;
    }

    setLoading(true);
    setError(null);
    setSearched(true);

    try {
      // publicService kullanılarak API çağrısı yapıldı
      const response = await publicService.getPublicOrderDetails(publicToken.trim());
      setOrderDetails(response.data);
    } catch (err) {
      console.error("Error fetching order details:", err);
      if (err.response?.status === 404) {
        setError("Bu sipariş kodu ile bir sipariş bulunamadı");
      } else if (err.response?.status === 400) {
        setError("Geçersiz sipariş kodu formatı");
      } else {
        setError("Sipariş detayları yüklenirken bir hata oluştu. Lütfen tekrar deneyin.");
      }
      setOrderDetails(null);
    } finally {
      setLoading(false);
    }
  };
  const handleInputChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (value.length <= 10) {
      setPublicToken(value);
      if (error && value.length === 10) {
        setError(null);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      fetchOrderDetails();
    }
  };

  const handleRetry = () => {
    fetchOrderDetails();
  };

  const handleReset = () => {
    setPublicToken("");
    setOrderDetails(null);
    setError(null);
    setSearched(false);
  };

  const columns = [
    {
      title: "Kalem İsmi",
      dataIndex: "itemName",
      key: "itemName",
    },
    {
      title: "Adet",
      dataIndex: "quantity",
      key: "quantity",
      align: "center",
    },
    {
      title: "Adet Fiyatı",
      dataIndex: "unitPrice",
      key: "unitPrice",
      align: "right",
      render: (price) => formatPrice(price),
    },
    {
      title: "Toplam",
      key: "total",
      align: "right",
      render: (text, record) => formatTotalPrice(record.quantity, record.unitPrice),
    },
    {
      title: "Durum",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (status) => {
        const statusConfig = itemStatusLabels[status] || { label: status, color: "default" };
        return (
          <Tag color={statusConfig.color}>
            {statusConfig.label}
          </Tag>
        );
      },
    },
    {
      title: "Notlar",
      dataIndex: "notes",
      key: "notes",
      render: (notes) => notes || "-",
    },
  ];

  const totalAmount = orderDetails?.orderItems?.reduce(
    (total, item) => {
      try {
        return total + item.quantity * (item.unitPrice || 0);
      } catch (error) {
        return total;
      }
    },
    0
  ) || 0;

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f5f7fa", padding: "40px 20px" }}>
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <Card
          title={
            <Title level={2} style={{ margin: 0, textAlign: "center", color: "#2c3e50" }}>
              Sipariş Takip Sistemi
            </Title>
          }
          style={{ marginBottom: 32, borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", border: "1px solid #e8e8e8" }}
        >
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            <div style={{ textAlign: "center" }}>
              <Text type="secondary" style={{ fontSize: "16px" }}>
                Sipariş kodunuzu girerek sipariş durumunu takip edebilirsiniz
              </Text>
            </div>
            
            <Row justify="center">
              <Col xs={24} sm={18} md={12} lg={10}>
                <Space.Compact style={{ width: "100%" }}>
                  <Input
                    placeholder="Sipariş Kodu (10 hane)"
                    value={publicToken}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    maxLength={10}
                    size="large"
                    style={{ 
                      textTransform: "uppercase",
                      fontSize: "16px"
                    }}
                    status={error ? "error" : ""}
                  />
                  <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    onClick={fetchOrderDetails}
                    loading={loading}
                    size="large"
                    disabled={!publicToken.trim() || publicToken.length !== 10}
                    style={{ 
                      backgroundColor: "#1890ff",
                      borderColor: "#1890ff",
                      fontWeight: "500"
                    }}
                  >
                    Sorgula
                  </Button>
                </Space.Compact>
              </Col>
            </Row>

            {error && (
              <Alert
                message={error}
                type="error"
                showIcon
                style={{ textAlign: "center", borderRadius: "6px" }}
                action={
                  searched && (
                    <Space>
                      <Button size="small" onClick={handleRetry}>
                        Tekrar Dene
                      </Button>
                      <Button size="small" onClick={handleReset}>
                        Yeni Arama
                      </Button>
                    </Space>
                  )
                }
              />
            )}
          </Space>
        </Card>

        {loading && (
          <div style={{ textAlign: "center", marginTop: "3rem" }}>
            <Spin tip="Sipariş detayları yükleniyor..." size="large" />
          </div>
        )}

        {orderDetails && !loading && (
          <>
            <Card
              title={
                <Title level={4} style={{ margin: 0, color: "#2c3e50" }}>
                  Sipariş Bilgileri
                </Title>
              }
              extra={
                <Button size="small" onClick={handleReset} type="default">
                  Yeni Arama
                </Button>
              }
              style={{ marginBottom: 24, borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", border: "1px solid #e8e8e8" }}
            >
              <Row gutter={[24, 24]}>
                <Col xs={24} sm={12} lg={8}>
                  <div style={{ padding: "16px", backgroundColor: "#fafafa", borderRadius: "6px", border: "1px solid #f0f0f0" }}>
                    <Text strong style={{ color: "#595959", fontSize: "14px" }}>Sipariş No</Text>
                    <br />
                    <Text style={{ fontSize: "16px", fontWeight: "500", color: "#262626" }}>#{orderDetails.id}</Text>
                  </div>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <div style={{ padding: "16px", backgroundColor: "#fafafa", borderRadius: "6px", border: "1px solid #f0f0f0" }}>
                    <Text strong style={{ color: "#595959", fontSize: "14px" }}>Firma</Text>
                    <br />
                    <Text style={{ fontSize: "16px", fontWeight: "500", color: "#262626" }}>{orderDetails.companyName}</Text>
                  </div>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <div style={{ padding: "16px", backgroundColor: "#fafafa", borderRadius: "6px", border: "1px solid #f0f0f0" }}>
                    <Text strong style={{ color: "#595959", fontSize: "14px" }}>Referans</Text>
                    <br />
                    <Text style={{ fontSize: "16px", fontWeight: "500", color: "#262626" }}>{orderDetails.orderReference || "-"}</Text>
                  </div>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <div style={{ padding: "16px", backgroundColor: "#fafafa", borderRadius: "6px", border: "1px solid #f0f0f0" }}>
                    <Text strong style={{ color: "#595959", fontSize: "14px" }}>Sipariş Tarihi</Text>
                    <br />
                    <Text style={{ fontSize: "16px", fontWeight: "500", color: "#262626" }}>
                      {formatDate(orderDetails.orderDate)}
                    </Text>
                  </div>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <div style={{ padding: "16px", backgroundColor: "#fafafa", borderRadius: "6px", border: "1px solid #f0f0f0" }}>
                    <Text strong style={{ color: "#595959", fontSize: "14px" }}>Durum</Text>
                    <br />
                    <Tag
                      color={orderStatusLabels[orderDetails.status]?.color || "default"}
                      style={{ fontSize: "14px", padding: "4px 12px", marginTop: "4px", fontWeight: "500" }}
                    >
                      {orderStatusLabels[orderDetails.status]?.label || orderDetails.status}
                    </Tag>
                  </div>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <div style={{ padding: "16px", backgroundColor: "#f6ffed", borderRadius: "6px", border: "1px solid #b7eb8f" }}>
                    <Text strong style={{ color: "#595959", fontSize: "14px" }}>Toplam Tutar</Text>
                    <br />
                    <Text style={{ fontSize: "20px", fontWeight: "600", color: "#389e0d" }}>
                      {formatPrice(totalAmount)}
                    </Text>
                  </div>
                </Col>
                {orderDetails.notes && (
                  <Col xs={24}>
                    <div style={{ padding: "16px", backgroundColor: "#fafafa", borderRadius: "6px", border: "1px solid #f0f0f0" }}>
                      <Text strong style={{ color: "#595959", fontSize: "14px" }}>Sipariş Notları</Text>
                      <br />
                      <Text style={{ fontSize: "16px", color: "#262626", lineHeight: "1.5" }}>{orderDetails.notes}</Text>
                    </div>
                  </Col>
                )}
              </Row>
            </Card>

            <Card 
              title={
                <Title level={4} style={{ margin: 0, color: "#2c3e50" }}>
                  Sipariş Kalemleri
                </Title>
              }
              style={{ borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", border: "1px solid #e8e8e8" }}
            >
              <Table
                dataSource={orderDetails.orderItems || []}
                columns={columns}
                rowKey="id"
                pagination={false}
                scroll={{ x: true }}
                style={{ backgroundColor: "white" }}
                locale={{
                  emptyText: "Sipariş kalemi bulunamadı"
                }}
                footer={() => (
                  orderDetails.orderItems && orderDetails.orderItems.length > 0 && (
                    <div style={{ padding: "16px 0", borderTop: "1px solid #f0f0f0", marginTop: "16px" }}>
                      <Row justify="space-between" align="middle">
                        <Col>
                          <Text style={{ fontSize: "16px", color: "#595959" }}>
                            Toplam <strong style={{ color: "#262626" }}>{orderDetails.orderItems.length}</strong> kalem
                          </Text>
                        </Col>
                        <Col>
                          <Text style={{ fontSize: "18px", fontWeight: "600", color: "#262626" }}>
                            Genel Toplam: <span style={{ color: "#389e0d" }}>{formatPrice(totalAmount)}</span>
                          </Text>
                        </Col>
                      </Row>
                    </div>
                  )
                )}
              />
            </Card>
          </>
        )}

        {searched && !orderDetails && !loading && !error && (
          <Result
            status="404"
            title="Sipariş Bulunamadı"
            subTitle="Girilen sipariş kodu ile bir sipariş bulunamadı."
            extra={
              <Button type="primary" onClick={handleReset} size="large">
                Yeni Arama Yap
              </Button>
            }
            style={{ 
              backgroundColor: "white", 
              borderRadius: "8px", 
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              border: "1px solid #e8e8e8"
            }}
          />
        )}
      </div>
    </div>
  );
};

export default PublicOrderDetailsPage;